<template>
  <ScrollTop v-if="showScrollTop" />
  <div class="englobe-params">
    <div class="params-row top">
      <FiltersMenu
        :active-filters="activeFilters"
        :is-loading="!store.filters"
        @remove-filter="removeFilterItem"
        @reset-all-filters="resetAllFilters"
      >
        <SearchAsYouTypeFilter
          v-if="!store.isFollawedActive"
          filter="author"
          :items="store.authors"
          item-label="name"
          item-value="_id"
          :active-filters="selectedFilters.author"
          :is-loading="store.isAuthorFilterLoading"
          @search="fetchSearchAuthors"
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetAuthorFilter"
        >
          Auteurs
        </SearchAsYouTypeFilter>
        <SelectMultipleCounter
          v-for="(value, key) in filtersNames"
          :key="key"
          :filter="key"
          :items="store.filters[key]"
          :active-filters="selectedFilters[key]"
          has-search
          @add-filter-item="addFilterItem"
          @remove-filter-item="removeFilterItem"
          @reset-filter="resetFilter"
        >
          {{ value }}
        </SelectMultipleCounter>
      </FiltersMenu>

      <FollawedRadioButtons
        all-match-route="/questions"
        follawed-route="/questions/follawed"
      />
    </div>

    <!--Stats-->
    <QuestionsStats v-if="!store.isFollawedActive && statsAvailable" />

    <div class="params-row">
      <TotalMatched :item-count="store.item_count" feminine />

      <div class="page-settings">
        <div class="export-wrapper">
          <ExportButton
            :disabled="store.loadingExcel"
            icon-name="file-spreadsheet"
            @click="store.downloadExcel()"
          >
            <span v-if="store.loadingExcel">(En cours)</span>
            <span v-else>Exporter en .xlsx</span>
          </ExportButton>
        </div>

        <DropdownMenu
          v-model="selectedSorterOption"
          :options="sortersOptions"
          label="Trier par :"
        />

        <DropdownMenu
          v-model="selectedLimitOption"
          :options="limitOptions"
          label="Questions par page :"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { computed } from "vue";
import FiltersMenu from "@follaw-fr/components/filter-menu/organisms/FiltersMenu.vue";
import SearchAsYouTypeFilter from "@follaw-fr/components/filter-menu/molecules/SearchAsYouTypeFilter.vue";
import SelectMultipleCounter from "@follaw-fr/components/filter-menu/molecules/SelectMultipleCounter.vue";
import FollawedRadioButtons from "@/follaw-fr/components/reusable/atoms/FollawedRouterRadioButtons.vue";
import QuestionsStats from "@follaw-fr/components/questions/organisms/QuestionsStats.vue";
import TotalMatched from "@follaw-fr/components/reusable/atoms/TotalMatched.vue";
import ExportButton from "@follaw-fr/components/reusable/atoms/ExportButton.vue";
import ScrollTop from "@follaw-fr/components/reusable/atoms/ScrollTop.vue";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";
import { useQuestionsStore } from "@follaw-fr/stores/questions";
import { useDropdown } from "@follaw-fr/composables/useDropdown";

const store = useQuestionsStore();
const { selectedFilters } = storeToRefs(store);

const fetchQuestionsList = async () => {
  selectedFilters.value.from = 0;
  await store.fetchQuestionsList();
  await store.fetchQuestionsStats();
};

const fetchSearchAuthors = (nameSearch) => {
  if (nameSearch.length > 0) {
    store.getSearchAuthorsFilters(nameSearch);
  } else {
    store.authors = [];
  }
};

const { options: sortersOptions, selectedValue: selectedSorterOption } =
  useDropdown({
    options: {
      created_at: "Date de publication",
      closed_at: "Date de réponse",
    },
    selectedFilters,
    filterKey: "sort_field", // Matches a key in initialQuestionsFilters
    onChange: fetchQuestionsList,
  });

const { options: limitOptions, selectedValue: selectedLimitOption } =
  useDropdown({
    options: { 10: "10", 20: "20", 50: "50", 200: "200" },
    selectedFilters,
    filterKey: "size", // Matches a key in initialQuestionsFilters
    onChange: fetchQuestionsList,
  });

// All filters in wanted display order with their labels
const filtersNames = {
  status: "Statut",
  type_question: "Type de question",
  circo_name: "Localité",
  group: "Groupe",
  ministry_responsible: "Ministère attributaire",
  institution: "Institution",
};

const activeFilters = computed(() => {
  let arr = [];
  // Put all selected options from all filters with said filter name in an array for the SelectedFilters component
  Object.keys(filtersNames).forEach((key) => {
    selectedFilters.value[key]?.forEach((filter) => {
      arr = [...arr, { filter: key, item: filter }];
    });
  });
  selectedFilters.value.author?.forEach((filter) => {
    arr = [
      ...arr,
      {
        filter: "author",
        item: filter,
      },
    ];
  });
  return arr;
});

const showScrollTop = computed(() => selectedFilters.value.size > 20);

const addFilterItem = (event) => {
  const targetFilters = selectedFilters.value[event.filter] ?? [];
  selectedFilters.value = {
    ...selectedFilters.value,
    [event.filter]: [...targetFilters, event.item],
  };
  fetchQuestionsList();
};

const removeFilterItem = (event) => {
  let targetFilters = selectedFilters.value[event.filter] ?? [];
  const itemToRemove = event.item;
  targetFilters = targetFilters.filter((el) => el !== itemToRemove);
  if (targetFilters.length === 0) {
    delete selectedFilters.value[event.filter];
  } else {
    selectedFilters.value = {
      ...selectedFilters.value,
      [event.filter]: targetFilters,
    };
  }
  fetchQuestionsList();
};

const resetFilter = (event) => {
  delete selectedFilters.value[event];
  fetchQuestionsList();
};

const resetAuthorFilter = () => {
  store.authors = [];
  delete selectedFilters.value.author;
  fetchQuestionsList();
};

const resetAllFilters = () => {
  store.resetFilters();
  fetchQuestionsList();
};

// Stats
const statsAvailable = computed(() => {
  return (
    store.top3Ministries?.length > 0 ||
    store.similarQuestions?.length > 0 ||
    store.questionsType?.length > 0
  );
});
</script>
